const generateRangeValues: (data: { min: number; max: number; decimal: number }) => number[] = ({
  min,
  max,
  decimal = 0,
}) => {
  return Array.from(
    new Array(Math.pow(10, decimal) * (max - min) + 1),
    (val, index) => +(index / Math.pow(10, decimal) + min).toFixed(decimal),
  );
};

type TVitalType =
  | 'HR'
  | 'CHOLESTEROL'
  | 'GLUCOSE'
  | 'SYSTOLIC'
  | 'DIASTOLIC'
  | 'TEMPERATURE'
  | 'BREATHING_RATE'
  | 'OXYGEN_SATURATION'
  | 'NEWS2';

type TVitalDataInfo = {
  unit: string;
  default: number;
  range: number[];
  alternativeUnit?: string;
  conversion?(value: number): string;
};
export const getVitalDataInfo = (type: TVitalType): TVitalDataInfo => {
  const allData: any = {
    HR: {
      unit: 'units.bpm',
      range: { min: 25, max: 220, decimal: 0 },
      default: 70,
    },
    CHOLESTEROL: {
      unit: 'units.mmol/l',
      alternativeUnit: 'units.mg/dl',
      range: { min: 2.5, max: 6.5, decimal: 1 },
      default: 5.5,
      conversion: (v: number) => (v * 18.0182).toFixed(1),
    },
    GLUCOSE: {
      unit: 'units.mmol/l',
      alternativeUnit: 'units.mg/dl',
      range: { min: 1, max: 38.9, decimal: 1 },
      default: 4,
      conversion: (v: number) => (v * 18.0182).toFixed(1),
    },
    SYSTOLIC: {
      unit: 'units.mmHg',
      range: { min: 20, max: 280, decimal: 0 },
      default: 120,
    },
    DIASTOLIC: {
      unit: 'units.mmHg',
      range: { min: 20, max: 280, decimal: 0 },
      default: 80,
    },
    TEMPERATURE: {
      unit: 'units.celsius',
      alternativeUnit: 'units.farenheit',
      range: { min: 35, max: 42, decimal: 1 },
      default: 36.5,
      conversion: (v: number) => ((v * 9) / 5 + 32).toFixed(1),
    },
    BREATHING_RATE: {
      unit: 'count/min',
      range: { min: 1, max: 50, decimal: 0 },
      default: 20,
    },
    OXYGEN_SATURATION: {
      unit: 'count/min',
      range: { min: 80, max: 100, decimal: 0 },
      default: 95,
    },
  };
  return {
    unit: allData[type].unit,
    default: allData[type].default,
    range: generateRangeValues(allData[type].range),
    alternativeUnit: allData[type].alternativeUnit,
    conversion: allData[type].conversion,
  };
};

/**
 * used for donut chart
 */
type TCalChartPercentParams = {
  min: number;
  max: number;
  current: number;
};
export const calculateChartPercent = ({ min, max, current }: TCalChartPercentParams): number => {
  const CHART_PERCENT_PADDING = 12;
  const percent =
    ((current - min) * (100 - CHART_PERCENT_PADDING)) / (max - min) + CHART_PERCENT_PADDING;

  if (percent > 100 - CHART_PERCENT_PADDING) {
    return 100 - CHART_PERCENT_PADDING;
  }
  if (percent < CHART_PERCENT_PADDING) {
    return CHART_PERCENT_PADDING;
  }
  return percent;
};

export const vitalTimelineURLs = {
  HR: {
    url: 'heart-rate',
  },
  CHOLESTEROL: {
    url: 'cholesterol',
  },
  GLUCOSE: {
    url: 'glucose',
  },
  SYSTOLIC: {
    url: 'blood-pressure',
    type: 'systolic_pressure',
  },
  DIASTOLIC: {
    url: 'blood-pressure',
    type: 'diastolic_pressure',
  },
  TEMPERATURE: {
    url: 'body-temperature',
  },
  BREATHING_RATE: {
    url: 'breath-rate',
  },
  OXYGEN_SATURATION: {
    url: 'oxygen-saturation',
  },
  NEWS2: {
    url: 'news-2',
  },
};

export const vitalHistoryMeta = {
  HR: {
    units: 'units.bpm',
    name: 'vital_alert.heart_rate',
    chartAvgLabel: 'vital_history.avg_measurement_of_hr',
    chartDataLabel: 'vital_history.your_measurement_of_hr',
    img: require('../assets/svg/icon-heart-white.svg').default,
    min: 25,
    max: 220,
  },
  CHOLESTEROL: {
    units: 'units.mmol/l',
    name: 'vital_alert.cholesterol',
    chartAvgLabel: 'vital_history.avg_measurement_of_cholesterol',
    chartDataLabel: 'vital_history.your_measurement_of_cholesterol',
    img: require('../assets/svg/icon-cholesterol-white.svg').default,
    min: 1,
    max: 6.5,
  },
  GLUCOSE: {
    units: 'units.mmol/l',
    name: 'vital_alert.glucose',
    chartAvgLabel: 'vital_history.avg_measurement_of_glucose',
    chartDataLabel: 'vital_history.your_measurement_of_glucose',
    img: require('../assets/svg/icon-glucose-white.svg').default,
    min: 2,
    max: 7,
  },
  SYSTOLIC: {
    units: 'units.mmHg',
    name: 'vital_alert.systolic_pressure',
    chartAvgLabel: 'vital_history.avg_measurement_of_systolic',
    chartDataLabel: 'vital_history.your_measurement_of_systolic',
    img: require('../assets/svg/icon-blood-pressure-white.svg').default,
    min: 25,
    max: 220,
  },
  DIASTOLIC: {
    units: 'units.mmHg',
    name: 'vital_alert.diastolic_pressure',
    chartAvgLabel: 'vital_history.avg_measurement_of_diastolic',
    chartDataLabel: 'vital_history.your_measurement_of_diastolic',
    img: require('../assets/svg/icon-blood-pressure-white.svg').default,
    min: 25,
    max: 220,
  },
  TEMPERATURE: {
    units: 'units.celsius',
    name: 'vital_alert.temperature',
    chartAvgLabel: 'vital_history.avg_measurement_of_temp',
    chartDataLabel: 'vital_history.your_measurement_of_temp',
    img: require('../assets/svg/icon-temperature-white.svg').default,
    min: 33,
    max: 41.5,
  },
  BREATHING_RATE: {
    units: 'units.brpm',
    name: 'vital_alert.breath_rate',
    chartAvgLabel: 'vital_history.avg_measurement_of_breathing_rate',
    chartDataLabel: 'vital_history.your_measurement_of_breathing_rate',
    img: require('../assets/svg/life_id-breath-freq-white.svg').default,
    min: 10,
    max: 40,
  },
  OXYGEN_SATURATION: {
    units: 'units.percent',
    name: 'vital_alert.saturation',
    chartAvgLabel: 'vital_history.avg_measurement_of_saturation',
    chartDataLabel: 'vital_history.your_measurement_of_saturation',
    img: require('../assets/svg/life_id-saturation-white.svg').default,
    min: 80,
    max: 120,
  },
  NEWS2: {
    units: 'units.point',
    name: 'vital_alert.news2',
    chartAvgLabel: 'vital_history.avg_measurement_of_saturation',
    chartDataLabel: 'vital_history.your_measurement_of_saturation',
    img: require('../assets/svg/life_id-saturation.svg').default,
    min: 80,
    max: 120,
  },
  BMI: {
    units: 'units.bmi',
    name: 'life_id.bmi_index',
    chartAvgLabel: 'vital_history.avg_measurement_of_hr',
    chartDataLabel: 'vital_history.your_measurement_of_hr',
    img: require('../assets/svg/icon-bmi_white.svg').default,
    min: 25,
    max: 220,
  },
  WAIST: {
    units: 'units.cm',
    name: 'life_id.waist',
    chartAvgLabel: 'vital_history.avg_measurement_of_hr',
    chartDataLabel: 'vital_history.your_measurement_of_hr',
    img: require('../assets/svg/waist-circumference_white.svg').default,
    min: 0,
    max: 300,
  },
};
